import { Injector, computed, inject, runInInjectionContext } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals'

import { lastValueFrom } from 'rxjs'

import {
  DuplicateKnowledgeBasePayload,
  KnowledgeBase,
  KnowledgeBaseDetails,
  KnowledgeBasesPage,
  NewKnowledgeBasePayload,
} from '@flowx/designer/modules/agent-builder/models/knowledge-base.entity'
import { KnowledgeBaseApi } from '@flowx/designer/modules/agent-builder/services/knoweledge-base.api'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'

type KnowledgeBaseStore = {
  knowledgeBases: Array<KnowledgeBase>
  loading: boolean
  query: string
  selectedKnowledgeBaseId: KnowledgeBase['id'] | null
  paramDirection: string
  showAddNewModal: boolean
  formSubmitted: boolean
}
const initialState: KnowledgeBaseStore = {
  knowledgeBases: [],
  loading: false,
  query: '',
  paramDirection: 'input',
  selectedKnowledgeBaseId: null,
  showAddNewModal: false,
  formSubmitted: false,
}

type CheckConnectionPayload = {
  name: string
  description: string
  type: string
  app_version_id: string
  application_id: string
  additional_props: any
}

export const KnowledgeBaseStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    filteredKnowledgeBases: computed(() => {
      const query = store.query()
      const knowledgeBases = store.knowledgeBases()

      if (!query?.length) {
        return knowledgeBases
      }
      return knowledgeBases.filter((knowledgeBase) =>
        knowledgeBase.name.toLowerCase().includes(query.toLowerCase())
      )
    }),
  })),
  withMethods((store, knowledgeBaseApiService = inject(KnowledgeBaseApi)) => {
    const injector = inject(Injector)
    return {
      getKnowledgeBases$: () =>
        runInInjectionContext(injector, () => toObservable(computed(() => store.knowledgeBases()))),
      setSelected: (serverId: KnowledgeBase['id']) => {
        patchState(store, { selectedKnowledgeBaseId: serverId })
      },
      filter: (query: string) => {
        patchState(store, { query })
      },
      resetSelected: () => {
        patchState(store, { selectedKnowledgeBaseId: null })
      },

      getKnowledgeBase: async (resourceId: string) => {
        const knowledgeBase = await lastValueFrom(
          knowledgeBaseApiService.getKnowledgeBaseById(resourceId)
        )
        return knowledgeBase
      },
      setFormSubmitted: (submitted: boolean) => {
        patchState(store, { formSubmitted: submitted })
      },
      checkConnection: async (knowledgeBase: CheckConnectionPayload) => {
        const connection = await lastValueFrom(
          knowledgeBaseApiService.checkConnection(knowledgeBase)
        )
        return connection
      },
      searchDocuments: async (knowledgeBaseId: KnowledgeBase['id'], query: string) => {
        const documents = await lastValueFrom(
          knowledgeBaseApiService.searchDocuments(knowledgeBaseId, query)
        )
        return documents
      },
      uploadDocument: async (knowledgeBaseId: KnowledgeBase['id'], formData: FormData) => {
        const document = await lastValueFrom(
          knowledgeBaseApiService.uploadDocument(knowledgeBaseId, formData)
        )
        return document
      },
      deleteDocument: async (knowledgeBaseId: KnowledgeBase['id'], filename: string) => {
        const result = await lastValueFrom(
          knowledgeBaseApiService.deleteDocument(knowledgeBaseId, filename)
        )
        return result
      },
      onShowAddNewModal: () => {
        patchState(store, { showAddNewModal: true })
      },
      onHideAddNewModal: () => {
        patchState(store, { showAddNewModal: false })
      },
      loadAll: async (appId: string) => {
        patchState(store, { loading: true })

        const knowledgeBases: KnowledgeBasesPage = await lastValueFrom(
          knowledgeBaseApiService.getKnowledgeBasesByApplicationId(appId)
        )
        patchState(store, {
          knowledgeBases: knowledgeBases.items.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          ),
          loading: false,
        })
        return knowledgeBases.items
      },
      create: async (server: NewKnowledgeBasePayload) => {
        const newKnowledgeBase = await lastValueFrom(
          knowledgeBaseApiService.createKnowledgeBase(server)
        )
        patchState(store, { knowledgeBases: [newKnowledgeBase, ...store.knowledgeBases()] })
        return newKnowledgeBase
      },
      duplicate: async (knowledgeBase: DuplicateKnowledgeBasePayload) => {
        const newKnowledgeBase: KnowledgeBase = await lastValueFrom(
          knowledgeBaseApiService.duplicateKnowledgeBase(knowledgeBase)
        )
        patchState(store, { knowledgeBases: [newKnowledgeBase, ...store.knowledgeBases()] })
        return newKnowledgeBase
      },
      update: async (
        knowledgeBase: Pick<
          KnowledgeBaseDetails,
          'id' | 'name' | 'description' | 'application_id' | 'app_version_id'
        >
      ) => {
        const existingKnowledgeBase = store.knowledgeBases().find((s) => s.id === knowledgeBase.id)

        const updatedKnowledgeBase = await lastValueFrom(
          knowledgeBaseApiService.updateKnowledgeBase({
            ...existingKnowledgeBase,
            ...knowledgeBase,
          })
        )

        patchState(store, {
          knowledgeBases: store.knowledgeBases().map((s) =>
            s.id === updatedKnowledgeBase.id
              ? {
                  ...existingKnowledgeBase,
                  name: updatedKnowledgeBase.name,
                  description: updatedKnowledgeBase.description,
                }
              : s
          ),
        })
      },

      delete: async (id: KnowledgeBase['id']) => {
        await lastValueFrom(knowledgeBaseApiService.deleteKnowledgeBase(id))
        patchState(store, {
          knowledgeBases: store.knowledgeBases().filter((s) => s.id !== id),
        })
      },
    }
  }),
  withComputed((store) => ({
    selected: computed(() =>
      store.knowledgeBases().find((s) => s.id === store.selectedKnowledgeBaseId())
    ),
  })),
  withHooks({
    onInit(store, urlParamStore = inject(UrlParametersStoreService)) {
      const { appId } = urlParamStore.urlParameters
      store.loadAll(appId)
    },
  })
)
